import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import {
  FaCheck,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
  FaInfoCircle,
  FaTrophy,
} from "react-icons/fa";
import { TournamentHeader } from "./GameSessionHeader";
import "./Style.css";

const emptyStaticPlaces = [
  { place: 1, points: 10 },
  { place: 2, points: 7 },
  { place: 3, points: 5 },
];

const DYNAMIC_MULTIPLIER_OPTIONS = [
  { label: "Reduced - 0.5x", value: 0.5 },
  { label: "Normal - 1x", value: 1 },
  { label: "Boosted - 2.5x", value: 2.5 },
  { label: "Maximum - 5x", value: 5 },
];

const makeDraft = (preset = null) => {
  const config = preset?.config_json || preset?.config || {};
  const multiplier = Number(config.multiplier) > 0 ? Number(config.multiplier) : 1;
  return {
    id: preset?.id || null,
    name: preset?.name || "",
    mode: preset?.mode || "dynamic",
    config:
      preset?.mode === "static"
        ? { places: config.places?.length ? config.places : emptyStaticPlaces }
        : { maxPlaces: config.maxPlaces ?? 10, multiplier },
  };
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString()}`;

const normalizePlayerSearchValue = (value) =>
  String(value || "").toLowerCase().trim();

const getPlayerFullName = (player = {}) =>
  [player.first_name || player.firstName, player.last_name || player.lastName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

const getPlayerSearchText = (player = {}) =>
  [
    player.name,
    player.nickname,
    player.first_name,
    player.last_name,
    player.firstName,
    player.lastName,
  ]
    .map(normalizePlayerSearchValue)
    .filter(Boolean)
    .join(" ");

const getPlayerOption = (player = {}) => ({
  label: player.name,
  value: player.id,
  fullName: getPlayerFullName(player),
  searchText: getPlayerSearchText(player),
  lastPlayedTournament: player.last_played_tournament || player.lastPlayedTournament || null,
});

const filterPlayerOption = ({ data }, inputValue) => {
  const searchValue = normalizePlayerSearchValue(inputValue);
  if (!searchValue) return true;
  return data.searchText?.includes(searchValue);
};

const formatPlayerOptionLabel = ({ label, fullName, lastPlayedTournament }) => {
  const showFullName =
    fullName &&
    normalizePlayerSearchValue(fullName) !== normalizePlayerSearchValue(label);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.18rem", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", minWidth: 0 }}>
        <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </span>
        {showFullName && (
          <span style={{ color: "#a0aec0", fontSize: "0.82rem", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            ({fullName})
          </span>
        )}
      </div>
      {!lastPlayedTournament && (
        <span style={{ color: "#f6ad55", fontSize: "0.74rem", fontWeight: 700 }}>
          No tournament history
        </span>
      )}
    </div>
  );
};

const selectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: "#2d3748",
    borderColor: "#4a5568",
    color: "#fff",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#2d3748",
    zIndex: 3000,
  }),
  option: (base, { isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? "#4a5568" : "#2d3748",
    color: "#e2e8f0",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4a5568",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e2e8f0",
  }),
  input: (base) => ({
    ...base,
    color: "#e2e8f0",
  }),
};

const preventNumberInputWheel = (e) => {
  if (e.target?.tagName === "INPUT" && e.target.type === "number") {
    e.preventDefault();
    e.target.blur();
  }
};

function InitialLoadingScreen() {
  return (
    <div
      className="loading-container"
      style={{ padding: "4rem", textAlign: "center", color: "white" }}
    >
      <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
        Gathering Data...
      </h2>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          className="spinner"
          style={{
            border: "4px solid rgba(255,255,255,0.1)",
            borderTop: "4px solid #6366f1",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            animation: "spin 1s linear infinite",
          }}
        ></div>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function TournamentPoints() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [entryPoints, setEntryPoints] = useState(0);
  const [draft, setDraft] = useState(makeDraft());
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [previewEntries, setPreviewEntries] = useState(30);
  const [confirmEndSeason, setConfirmEndSeason] = useState(false);
  const [deletePresetModal, setDeletePresetModal] = useState({ isOpen: false, preset: null });
  const [isEditingPresetName, setIsEditingPresetName] = useState(false);
  const [savingEntryPoints, setSavingEntryPoints] = useState(false);
  const [endingSeason, setEndingSeason] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manualPointsModal, setManualPointsModal] = useState({
    isOpen: false,
    players: [],
    selectedPlayers: [],
    amount: "",
    loading: false,
    applying: false,
  });

  const isAnyModalOpen =
    manualPointsModal.isOpen ||
    selectedPlayer !== null ||
    deletePresetModal.isOpen ||
    confirmEndSeason;

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    }
    if (!permission?.includes("O") && !permission?.includes("Y")) {
      navigate(`/${cardroom}/game`);
      return;
    }
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }, [token, cardroom, navigate, dealerMode, permission]);

  useEffect(() => {
    document.body.style.overflow = isAnyModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isAnyModalOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: settings }, { data: board }] = await Promise.all([
        axios.get(`/${cardroom}/api/tournaments/points/settings`),
        axios.get(`/${cardroom}/api/tournaments/points/leaderboard`),
      ]);
      setPresets(settings.presets || []);
      setEntryPoints(settings.entryPoints || 0);
      setLeaderboard(board.players || []);
      const active = (settings.presets || []).find((preset) => preset.active) || (settings.presets || [])[0];
      setDraft(makeDraft(active));
    } catch (err) {
      console.error("Failed to load tournament points", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData();
  }, [token, cardroom]);

  const previewRows = useMemo(() => {
    const presetMultiplier =
      Number(draft.config.multiplier) > 0 ? Number(draft.config.multiplier) : 1;
    if (draft.mode === "static") {
      return (draft.config.places || []).map((row, index) => ({
        place: index + 1,
        points: Math.round(Number(row.points) || 0),
      }));
    }

    const maxPlaces = Math.max(0, Number(draft.config.maxPlaces) || 0);
    const count = maxPlaces || Math.max(1, Number(previewEntries) || 1);
    return Array.from({ length: count }, (_, index) => {
      const place = index + 1;
      return {
        place,
        points: Math.round(
          Math.sqrt((Number(previewEntries) || 1) / place) * presetMultiplier,
        ),
      };
    });
  }, [draft, previewEntries]);

  const activePreset = presets.find((preset) => preset.active);
  const selectedPreset = presets.find((preset) => Number(preset.id) === Number(draft.id));
  const previewColumns = useMemo(() => {
    if (previewRows.length <= 10) return [previewRows];
    const midpoint = Math.ceil(previewRows.length / 2);
    return [previewRows.slice(0, midpoint), previewRows.slice(midpoint)];
  }, [previewRows]);

  const saveEntryPoints = async () => {
    setSavingEntryPoints(true);
    try {
      await axios.patch(`/${cardroom}/api/tournaments/points/entry-points`, {
        entryPoints: Number(entryPoints) || 0,
      });
      const { data: settings } = await axios.get(`/${cardroom}/api/tournaments/points/settings`);
      setEntryPoints(settings.entryPoints || 0);
      setPresets(settings.presets || []);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save entry points");
    } finally {
      setSavingEntryPoints(false);
    }
  };

  const savePreset = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      let savedPreset = null;
      if (draft.id) {
        const { data } = await axios.patch(`/${cardroom}/api/tournaments/points/presets/${draft.id}`, draft);
        savedPreset = data;
      } else {
        const { data } = await axios.post(`/${cardroom}/api/tournaments/points/presets`, draft);
        savedPreset = data;
      }

      const { data: settings } = await axios.get(`/${cardroom}/api/tournaments/points/settings`);
      const nextPresets = settings.presets || [];
      const refreshedPreset =
        nextPresets.find((preset) => Number(preset.id) === Number(savedPreset?.id)) ||
        savedPreset;

      setPresets(nextPresets);
      setEntryPoints(settings.entryPoints || 0);
      setDraft(makeDraft(refreshedPreset));
      setIsEditingPresetName(false);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save point preset");
    } finally {
      setSaving(false);
    }
  };

  const activatePreset = async (presetId) => {
    setSaving(true);
    try {
      await axios.post(`/${cardroom}/api/tournaments/points/presets/${presetId}/activate`);
      const { data: settings } = await axios.get(`/${cardroom}/api/tournaments/points/settings`);
      setPresets(settings.presets || []);
      setEntryPoints(settings.entryPoints || 0);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to activate preset");
    } finally {
      setSaving(false);
    }
  };

  const deletePreset = async () => {
    const preset = deletePresetModal.preset;
    if (!preset) return;
    setSaving(true);
    try {
      await axios.delete(`/${cardroom}/api/tournaments/points/presets/${preset.id}`);
      setPresets(prev => {
        const nextPresets = prev.filter((item) => Number(item.id) !== Number(preset.id));
        if (Number(draft.id) === Number(preset.id)) {
          const active = nextPresets.find((item) => item.active) || nextPresets[0];
          setDraft(active ? makeDraft(active) : makeDraft());
          setIsEditingPresetName(!active);
        }
        return nextPresets;
      });
      setDeletePresetModal({ isOpen: false, preset: null });
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete preset");
    } finally {
      setSaving(false);
    }
  };

  const endSeason = async () => {
    setEndingSeason(true);
    try {
      await axios.post(`/${cardroom}/api/tournaments/points/end-season`);
      setConfirmEndSeason(false);
      setLeaderboard([]);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to end season");
    } finally {
      setEndingSeason(false);
    }
  };

  const openManualPointsModal = async () => {
    setManualPointsModal({
      isOpen: true,
      players: [],
      selectedPlayers: [],
      amount: "",
      loading: true,
      applying: false,
    });
    try {
      const { data } = await axios.get(`/${cardroom}/api/players`);
      setManualPointsModal((prev) => ({
        ...prev,
        players: data || [],
        loading: false,
      }));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to load players");
      setManualPointsModal({
        isOpen: false,
        players: [],
        selectedPlayers: [],
        amount: "",
        loading: false,
        applying: false,
      });
    }
  };

  const closeManualPointsModal = () => {
    setManualPointsModal({
      isOpen: false,
      players: [],
      selectedPlayers: [],
      amount: "",
      loading: false,
      applying: false,
    });
  };

  const applyManualPoints = async () => {
    const playerIds = manualPointsModal.selectedPlayers.map((player) => player.value);
    const points = Math.round(Number(manualPointsModal.amount) || 0);
    if (!playerIds.length || points <= 0 || manualPointsModal.applying) return;

    setManualPointsModal((prev) => ({ ...prev, applying: true }));
    try {
      const { data } = await axios.post(`/${cardroom}/api/tournaments/points/manual`, {
        playerIds,
        points,
      });
      setLeaderboard(data.players || []);
      closeManualPointsModal();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to add manual points");
      setManualPointsModal((prev) => ({ ...prev, applying: false }));
    }
  };

  const setStaticPlacePoints = (index, points) => {
    const places = [...(draft.config.places || [])];
    places[index] = { place: index + 1, points };
    setDraft({ ...draft, config: { ...draft.config, places } });
  };

  const removeStaticPlace = (index) => {
    const places = (draft.config.places || [])
      .filter((_, placeIndex) => placeIndex !== index)
      .map((row, placeIndex) => ({ ...row, place: placeIndex + 1 }));
    setDraft({
      ...draft,
      config: {
        ...draft.config,
        places: places.length ? places : [{ place: 1, points: 0 }],
      },
    });
  };

  if (loading) {
    return (
      <div className="game">
        <TournamentHeader activePage="points" cardroom={cardroom} centerTitle="Tournament Points" title="Tournaments" />
        <div className="game-container">
          <InitialLoadingScreen />
        </div>
      </div>
    );
  }

  return (
    <div className="game" onWheelCapture={preventNumberInputWheel}>
      <TournamentHeader activePage="points" cardroom={cardroom} centerTitle="Tournament Points" title="Tournaments" />

      <div className="game-container">
<section className="game-section">
  <h2 className="game-section__title">
    <FaTrophy /> Season Points
  </h2>
  <div className="game-form" style={{ alignItems: "end" }}>
    <div>
      <label className="game-label">Points for Playing</label>
      <input
        type="number"
        min="0"
        className="game-input"
        value={entryPoints}
        onChange={(e) => setEntryPoints(e.target.value)}
      />
    </div>
    <button className="game-btn game-btn--primary" type="button" onClick={saveEntryPoints} disabled={savingEntryPoints}>
      {savingEntryPoints ? "Saving..." : <><FaSave /> Save</>}
    </button>
    <button className="game-btn game-btn--danger" type="button" onClick={() => setConfirmEndSeason(true)} disabled={saving}>
      End Season
    </button>

    <div style={{
      marginRight: "auto",
      flex: 1,
      minWidth: "200px",
      maxWidth: "1000px",
      border: "1px solid rgba(180, 140, 60, 0.35)",
      borderRadius: "8px",
      padding: "10px 14px",
      background: "rgba(180, 140, 60, 0.06)",
    }}>
      <p style={{ margin: 0, fontSize: "12.5px", color: "#b89050", lineHeight: 1.5 }}>
        <FaInfoCircle style={{ verticalAlign: "-1px", marginRight: "5px", fontSize: "13px" }} />
        Only awarded to players with <strong style={{ color: "#c9a060" }}>zero</strong> placement points, a guaranteed minimum for anyone who played but never placed.
      </p>
    </div>
  </div>
</section>

        <section className="game-section">
          <h2 className="game-section__title">Point Presets</h2>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(230px, 0.35fr) minmax(420px, 1fr)", gap: "1rem", alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {activePreset && (
                <div
                  style={{
                    padding: "0.85rem",
                    border: "1px solid rgba(250, 204, 21, 0.35)",
                    borderRadius: 8,
                    background: "rgba(250, 204, 21, 0.08)",
                  }}
                >
                  <div style={{ color: "#facc15", fontWeight: 800, fontSize: "0.75rem", textTransform: "uppercase" }}>
                    Active Preset
                  </div>
                  <div style={{ fontWeight: 800, marginTop: "0.25rem" }}>{activePreset.name}</div>
                </div>
              )}
              {presets.map((preset) => (
                <div
                  key={preset.id}
                  className="game-card"
                  style={{
                    padding: "1rem",
                    border: preset.active
                      ? "1px solid #facc15"
                      : Number(draft.id) === Number(preset.id)
                        ? "1px solid var(--game-primary)"
                        : "1px solid var(--game-border)",
                    background:
                      Number(draft.id) === Number(preset.id)
                        ? "rgba(56, 189, 248, 0.08)"
                        : "var(--game-bg-secondary)",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setDraft(makeDraft(preset));
                    setIsEditingPresetName(false);
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center" }}>
                    <div>
                      <strong>{preset.name}</strong>
                      <div style={{ color: "var(--game-text-secondary)", textTransform: "capitalize" }}>
                        {preset.mode} &bull; {Number((preset.config_json || {}).multiplier || 1)}x
                      </div>
                      {preset.active && (
                        <div style={{ color: "#facc15", fontSize: "0.75rem", fontWeight: 800, marginTop: 4 }}>
                          ACTIVE
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      {!preset.active && (
                        <button className="game-icon-btn game-icon-btn--success" type="button" onClick={(e) => { e.stopPropagation(); activatePreset(preset.id); }} title="Activate">
                          <FaCheck />
                        </button>
                      )}
                      {!preset.active && (
                        <button className="game-icon-btn game-icon-btn--danger" type="button" onClick={(e) => { e.stopPropagation(); setDeletePresetModal({ isOpen: true, preset }); }} title="Delete">
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <button
                className="game-btn game-btn--secondary"
                type="button"
                onClick={() => {
                  setDraft(makeDraft());
                  setIsEditingPresetName(true);
                }}
              >
                <FaPlus /> New Preset
              </button>
            </div>

            <div className="game-card" style={{ padding: "1rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 1fr) minmax(240px, 0.65fr)", gap: "1.25rem", alignItems: "start" }}>
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "1rem",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <div>
                      <div style={{ color: "var(--game-text-secondary)", fontSize: "0.8rem", fontWeight: 700 }}>
                        Editing
                      </div>
                      {isEditingPresetName ? (
                        <input
                          className="game-input"
                          autoFocus
                          value={draft.name}
                          onBlur={() => setIsEditingPresetName(false)}
                          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setIsEditingPresetName(false);
                          }}
                          style={{ marginTop: "0.25rem", maxWidth: 320 }}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setIsEditingPresetName(true)}
                          style={{
                            background: "transparent",
                            border: 0,
                            color: "var(--game-text)",
                            cursor: "text",
                            fontSize: "1.15rem",
                            fontWeight: 900,
                            padding: 0,
                            textAlign: "left",
                          }}
                          title="Click to edit preset name"
                        >
                          {draft.name || selectedPreset?.name || "New Preset"}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      {selectedPreset?.active && (
                        <span
                          style={{
                            color: "#facc15",
                            border: "1px solid rgba(250, 204, 21, 0.45)",
                            borderRadius: 999,
                            padding: "0.35rem 0.65rem",
                            fontWeight: 800,
                          }}
                        >
                          Active
                        </span>
                      )}
                      <button className="game-btn game-btn--primary" type="button" onClick={savePreset} disabled={saving || !draft.name.trim()}>
                        {saving ? "Saving..." : <><FaSave /> Save</>}
                      </button>
                    </div>
                  </div>
                  <div className="game-form">
                    <div>
                      <label className="game-label">Type</label>
                      <select
                        className="game-input"
                        value={draft.mode}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            mode: e.target.value,
                            config:
                              e.target.value === "static"
                                ? { places: emptyStaticPlaces }
                                : { maxPlaces: 10, multiplier: draft.config.multiplier || 1 },
                          })
                        }
                      >
                        <option value="dynamic">Dynamic</option>
                        <option value="static">Static</option>
                      </select>
                    </div>
                    {draft.mode === "dynamic" && (
                      <div>
                        <label className="game-label">Line Scaling</label>
                        <select
                          className="game-input"
                          value={
                            DYNAMIC_MULTIPLIER_OPTIONS.some(
                              (option) => Number(option.value) === Number(draft.config.multiplier || 1),
                            )
                              ? draft.config.multiplier || 1
                              : ""
                          }
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              config: { ...draft.config, multiplier: Number(e.target.value) },
                            })
                          }
                        >
                          <option value="" disabled>
                            Custom - {Number(draft.config.multiplier || 1).toFixed(1)}x
                          </option>
                          {DYNAMIC_MULTIPLIER_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {draft.mode === "dynamic" ? (
                    <div style={{ marginTop: "1rem" }}>
                      <div className="game-form">
                        <div>
                          <label className="game-label">Only First X Finishers</label>
                          <input
                            type="number"
                            min="0"
                            className="game-input"
                            value={draft.config.maxPlaces}
                            onChange={(e) => setDraft({ ...draft, config: { ...draft.config, maxPlaces: e.target.value } })}
                          />
                        </div>
                        <div>
                          <label className="game-label">Preview Entrants</label>
                          <input type="number" min="1" className="game-input" value={previewEntries} onChange={(e) => setPreviewEntries(e.target.value)} />
                        </div>
                      </div>
                      <div style={{ marginTop: "1rem" }}>
                        <label className="game-label" style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
                          <span>Multiplier Line</span>
                          <strong>{Number(draft.config.multiplier || 1).toFixed(1)}x</strong>
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="5"
                          step="0.1"
                          value={draft.config.multiplier || 1}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              config: { ...draft.config, multiplier: Number(e.target.value) },
                            })
                          }
                          style={{ width: "100%" }}
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            color: "var(--game-text-secondary)",
                            fontSize: "0.8rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          <span>0.5x</span>
                          <span>5x</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: "1rem" }}>
                      {(draft.config.places || []).map((row, index) => (
                        <div key={index} className="game-form" style={{ marginBottom: "0.5rem", alignItems: "center" }}>
                          <strong style={{ minWidth: 80 }}>Place {index + 1}</strong>
                          <input type="number" min="0" className="game-input" value={row.points} onChange={(e) => setStaticPlacePoints(index, e.target.value)} />
                          <button className="game-icon-btn game-icon-btn--danger" type="button" onClick={() => removeStaticPlace(index)}>
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                      <button
                        className="game-btn game-btn--secondary"
                        type="button"
                        onClick={() =>
                          setDraft({
                            ...draft,
                            config: {
                              ...draft.config,
                              places: [
                                ...(draft.config.places || []),
                                { place: (draft.config.places || []).length + 1, points: 0 },
                              ],
                            },
                          })
                        }
                      >
                        <FaPlus /> Add Place
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="game-section__title" style={{ fontSize: "1rem" }}>Preview</h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        previewColumns.length > 1
                          ? "repeat(2, minmax(130px, 1fr))"
                          : "1fr",
                      gap: "0.75rem",
                    }}
                  >
                    {previewColumns.map((rows, columnIndex) => (
                      <div className="game-table-container" key={columnIndex}>
                        <table className="game-table">
                          <thead>
                            <tr>
                              <th>Place</th>
                              <th>Points</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row) => (
                              <tr key={row.place}>
                                <td>{row.place}</td>
                                <td>{row.points}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section className="game-section">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", marginBottom: "1rem" }}>
            <h2 className="game-section__title" style={{ margin: 0 }}>Leaderboard</h2>
            <button
              type="button"
              className="game-btn game-btn--secondary game-btn--sm"
              onClick={openManualPointsModal}
              style={{ marginLeft: "auto" }}
            >
              <FaPlus /> Add Points Manually
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "0.75rem",
            }}
          >
            {leaderboard.map((player, index) => (
              <button
                key={player.id}
                type="button"
                onClick={() => setSelectedPlayer(player)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "64px 1fr minmax(120px, auto) minmax(120px, auto)",
                  alignItems: "center",
                  gap: "1rem",
                  width: "100%",
                  padding: "1rem",
                  borderRadius: 8,
                  border: index === 0 ? "1px solid rgba(250, 204, 21, 0.55)" : "1px solid var(--game-border)",
                  background:
                    index === 0
                      ? "linear-gradient(90deg, rgba(250, 204, 21, 0.14), rgba(45, 55, 72, 0.75))"
                      : "var(--game-bg-secondary)",
                  color: "var(--game-text)",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: index === 0 ? "rgba(250, 204, 21, 0.18)" : "rgba(56, 189, 248, 0.12)",
                    color: index === 0 ? "#facc15" : "var(--game-primary)",
                    fontWeight: 900,
                    border: index === 0 ? "1px solid rgba(250, 204, 21, 0.35)" : "1px solid rgba(56, 189, 248, 0.25)",
                  }}
                >
                  {index + 1}
                </span>
                <span>
                  <strong style={{ fontSize: "1.05rem" }}>{player.name}</strong>
                  <span style={{ display: "block", color: "var(--game-text-secondary)", marginTop: 3 }}>
                    {player.records?.length || 0} scoring tournaments
                  </span>
                </span>
                <span style={{ textAlign: "center" }}>
                  <span style={{ display: "block", color: "var(--game-text-secondary)", fontSize: "0.75rem", fontWeight: 800 }}>
                    Points
                  </span>
                  <strong style={{ fontSize: "1.35rem", color: "#facc15" }}>{player.tournament_points}</strong>
                </span>
                <span style={{ textAlign: "right", color: "var(--game-text-secondary)" }}>
                  View history
                </span>
              </button>
            ))}
            {!leaderboard.length && <div className="game-empty">No tournament points yet.</div>}
          </div>
        </section>
      </div>

      {manualPointsModal.isOpen && (
        <>
          <div className="game-modal-overlay" onClick={manualPointsModal.applying ? undefined : closeManualPointsModal} />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Add Points Manually</h3>
              <button className="game-icon-btn" type="button" onClick={closeManualPointsModal} disabled={manualPointsModal.applying}>
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              {manualPointsModal.loading ? (
                <p className="game-empty">Loading players...</p>
              ) : (
                <>
                  <label className="game-label">Players</label>
                  <Select
                    isMulti
                    isSearchable
                    options={manualPointsModal.players.map(getPlayerOption)}
                    value={manualPointsModal.selectedPlayers}
                    onChange={(value) =>
                      setManualPointsModal((prev) => ({
                        ...prev,
                        selectedPlayers: value || [],
                      }))
                    }
                    placeholder="Search and select players..."
                    className="game-select-container"
                    classNamePrefix="game-select"
                    styles={selectStyles}
                    filterOption={filterPlayerOption}
                    formatOptionLabel={formatPlayerOptionLabel}
                  />

                  <label className="game-label" style={{ marginTop: "1rem" }}>Point Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="game-input"
                    value={manualPointsModal.amount}
                    onChange={(e) =>
                      setManualPointsModal((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    placeholder="Points to add"
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.5rem",
                      marginTop: "0.75rem",
                      color: "var(--game-text-secondary)",
                      fontSize: "0.9rem",
                      lineHeight: 1.4,
                    }}
                  >
                    <FaInfoCircle style={{ color: "var(--game-primary)", marginTop: "0.15rem", flexShrink: 0 }} />
                    <span>
                      Manual points update the selected players' leaderboard totals, but the manual addition itself will not appear in tournament history.
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={closeManualPointsModal}
                disabled={manualPointsModal.applying}
              >
                Cancel
              </button>
              <button
                type="button"
                className="game-btn game-btn--primary"
                onClick={applyManualPoints}
                disabled={
                  manualPointsModal.loading ||
                  manualPointsModal.applying ||
                  manualPointsModal.selectedPlayers.length === 0 ||
                  (Math.round(Number(manualPointsModal.amount) || 0) <= 0)
                }
              >
                {manualPointsModal.applying ? "Applying..." : "Apply Points"}
              </button>
            </div>
          </div>
        </>
      )}

      {selectedPlayer && (
        <>
          <div className="game-modal-overlay" onClick={() => setSelectedPlayer(null)} />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">{selectedPlayer.name} Points</h3>
              <button className="game-icon-btn" type="button" onClick={() => setSelectedPlayer(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const records = [...(selectedPlayer.records || [])].sort(
                  (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
                );

                if (!records.length) {
                  return <p className="game-empty">No tournament point records.</p>;
                }

                return (
                  <div className="game-transaction-list">
                    {records.map((record) => (
                      <div
                        key={`${record.tournament_id}-${record.created_at}-${record.points}`}
                        className="game-transaction-item"
                        style={{ flexWrap: "nowrap" }}
                      >
                        <strong
                          className="game-transaction-player"
                          style={{ minWidth: 0 }}
                        >
                          {record.tournament_name}
                        </strong>
                        <span
                          className="game-transaction-amount"
                          style={{ color: "#facc15", minWidth: "70px" }}
                        >
                          {record.points} pts
                        </span>
                        <span className="game-transaction-type" style={{ marginLeft: "auto" }}>
                          Place {record.place || "-"}
                        </span>
                        <span className="game-transaction-method">
                          Cashed {money(record.cash)}
                        </span>
                        <span className="game-transaction-date" style={{ marginLeft: "auto" }}>
                          {formatDate(record.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {deletePresetModal.isOpen && (
        <>
          <div className="game-modal-overlay" onClick={() => setDeletePresetModal({ isOpen: false, preset: null })} />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Delete Preset?</h3>
              <button className="game-icon-btn" type="button" onClick={() => setDeletePresetModal({ isOpen: false, preset: null })}>
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p>
                Delete <strong>{deletePresetModal.preset?.name}</strong>? This inactive point preset will be removed.
              </p>
              <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
                <button className="game-btn game-btn--secondary" type="button" onClick={() => setDeletePresetModal({ isOpen: false, preset: null })} disabled={saving}>
                  Cancel
                </button>
                <button className="game-btn game-btn--danger" type="button" onClick={deletePreset} disabled={saving}>
                  {saving ? "Deleting..." : "Delete Preset"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {confirmEndSeason && (
        <>
          <div className="game-modal-overlay" onClick={() => setConfirmEndSeason(false)} />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">End Season?</h3>
              <button className="game-icon-btn" type="button" onClick={() => setConfirmEndSeason(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p>This resets every player's current tournament points to 0. Tournament history stays untouched.</p>
              <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
                <button className="game-btn game-btn--secondary" type="button" onClick={() => setConfirmEndSeason(false)}>Cancel</button>
                <button className="game-btn game-btn--danger" type="button" onClick={endSeason} disabled={endingSeason}>
                  {endingSeason ? "Ending..." : "End Season"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
